<template>
  <Teleport to="body">
    <!-- ========== PRIMARY OVERLAY ========== -->
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 bg-black/40 z-[998]"
        @click="handleOverlayClick"
        @touchend.prevent="handleOverlayClick"
      ></div>
    </Transition>

    <!-- ========== PRIMARY DRAWER ========== -->
    <Transition :name="transitionName">
      <div
        v-if="isOpen"
        ref="drawerPanel"
        :class="[drawerPanelClasses, primaryMinimizeClass]"
        :style="primaryDrawerStyle">
        <div
          class="shrink-0"
          :class="{ 'cursor-grab touch-none': !isDesktop }"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <!-- Mobile drag handle -->
          <div v-if="!isDesktop" class="w-full pt-3 pb-1 flex justify-center">
            <div class="w-10 h-1.5 rounded-full" :class="drawerOptions.bgColor ? 'bg-white/30' : 'bg-gray-300'"></div>
          </div>

          <div v-if="drawerOptions.title || showPrimaryCloseButton || drawerOptions.onBack" :class="['p-6 flex justify-between items-center', isDesktop ? 'pt-6' : 'pt-3']">
            <div class="flex items-center gap-3 min-w-0">
              <button
                v-if="drawerOptions.onBack"
                @click.stop="drawerOptions.onBack()"
                @touchstart.stop
                class="w-10 h-10 flex-none flex items-center justify-center focus:outline-none touch-none"
                :class="drawerOptions.bgColor ? 'bg-white/10 text-white/70 hover:bg-white/20' : ''"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7-7 7M21 12H3" />
                </svg>
              </button>
              <h2
                v-if="drawerOptions.title"
                class="text-2xl font-semibold touch-none text-start pe-4 truncate"
                :class="drawerOptions.bgColor ? 'text-white' : 'text-gray-900'"
              >
                {{ drawerOptions.title }}
              </h2>
            </div>
            <button
              v-if="showPrimaryCloseButton"
              @click.stop="closeDrawer"
              @touchstart.stop
              class="w-10 h-10 rounded-full flex-none flex items-center justify-center transition-colors focus:outline-none touch-none"
              :class="drawerOptions.bgColor ? 'bg-white/10 text-white/70 hover:bg-white/20' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div v-else class="w-10 h-10"></div>
          </div>
        </div>

        <div
          ref="drawerContent"
          :class="['flex-1 relative overflow-hidden flex flex-col', drawerOptions.noPadding ? 'px-0' : 'px-6']"
          :style="{paddingBottom: `calc(${insets.bottomPadding})`}"
        >
          <component :is="activeComponent" :key="drawerKey" v-bind="componentProps" class="h-full" />
        </div>
      </div>
    </Transition>

    <!-- ========== SECONDARY OVERLAY ========== -->
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="secondaryOpen"
        class="fixed inset-0 bg-black/20 z-[1000]"
        @click="handleSecondaryOverlayClick"
        @touchend.prevent="handleSecondaryOverlayClick"
      ></div>
    </Transition>

    <!-- ========== SECONDARY DRAWER ========== -->
    <Transition :name="secondaryTransitionName">
      <div
        v-if="secondaryOpen"
        ref="secondaryPanel"
        :class="secondaryPanelClasses"
        :style="secondaryDrawerStyle">
        <div
          class="shrink-0"
          :class="{ 'cursor-grab touch-none': !isDesktop }"
          @touchstart="handleSecondaryTouchStart"
          @touchmove="handleSecondaryTouchMove"
          @touchend="handleSecondaryTouchEnd"
        >
          <!-- Mobile drag handle -->
          <div v-if="!isDesktop" class="w-full pt-3 pb-1 flex justify-center">
            <div class="w-10 h-1.5 bg-gray-300 rounded-full"></div>
          </div>

          <div v-if="secondaryOptions.title || showSecondaryCloseButton" :class="['p-6 flex justify-between items-center', isDesktop ? 'pt-6' : 'pt-3']">
            <h2
              v-if="secondaryOptions.title"
              class="text-2xl font-semibold text-gray-900 touch-none text-right"
            >
              {{ secondaryOptions.title }}
            </h2>
            <button
              v-if="showSecondaryCloseButton"
              @click.stop="closeDrawer"
              @touchstart.stop
              class="w-10 h-10 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors focus:outline-none touch-none"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                   stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div v-else class="w-10 h-10"></div>
          </div>
        </div>

        <div
          ref="secondaryContent"
          :class="['flex-1 relative overflow-hidden flex flex-col', secondaryOptions.noPadding ? 'px-0' : 'px-6']"
          :style="{paddingBottom: `calc(${insets.bottomPadding})`}"
        >
          <component :is="secondaryComponent" :key="secondaryKey" v-bind="secondaryProps" class="h-full" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDrawer } from '../composables/useDrawer';
import { useSharedStore } from '../store/shared-store';

const {
  isOpen, activeComponent, componentProps, drawerOptions, isDesktop, closeDrawer, drawerKey,
  secondaryOpen, secondaryComponent, secondaryProps, secondaryOptions, secondaryKey
} = useDrawer();
const sharedStore = useSharedStore();

// --- Primary drawer drag state ---
const drawerContent = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const startY = ref(0);
const currentY = ref(0);
const isInteractive = ref(false);

// --- Secondary drawer drag state ---
const secondaryContent = ref<HTMLElement | null>(null);
const secondaryIsDragging = ref(false);
const secondaryStartY = ref(0);
const secondaryCurrentY = ref(0);
const secondaryIsInteractive = ref(false);

const insets = computed<{ topPadding: string; bottomPadding: string; }>(() => {
  return sharedStore.getInset;
});

const showPrimaryCloseButton = computed(() => drawerOptions.value.closable && !drawerOptions.value.hideCloseButton);
const showSecondaryCloseButton = computed(() => secondaryOptions.value.closable && !secondaryOptions.value.hideCloseButton);

// ============ PRIMARY DRAWER ============

const primaryDrawerStyle = computed(() => {
  const style: Record<string, any> = {
    backgroundColor: drawerOptions.value.bgColor || 'white',
  };

  if (isDragging.value && !isDesktop.value) {
    style.transform = `translateY(${Math.max(0, currentY.value)}px)`;
    style.transition = 'none';
  }

  if (secondaryOpen.value) {
    if (isDesktop.value) {
      return {
        ...style,
        transform: 'translateY(-50%) scale(0.93)',
        opacity: '0.6',
        pointerEvents: 'none' as const,
      };
    } else {
      return { ...style, pointerEvents: 'none' as const };
    }
  }

  return style;
});

// CSS class for smooth minimize/un-minimize transitions
const primaryMinimizeClass = computed(() => {
  if (!isDragging.value) return 'primary-minimize-transition';
  return '';
});

const drawerPanelClasses = computed(() => {
  const baseClasses = 'fixed z-[999] flex flex-col will-change-transform';

  if (isDesktop.value) {
    return [
      baseClasses,
      'left-6 top-1/2 -translate-y-1/2 w-[480px] max-w-[90vw] h-[90vh] rounded-2xl shadow-2xl overflow-hidden',
    ].join(' ');
  } else if (drawerOptions.value.fullMobile) {
    // True fullscreen on mobile: covers everything including safe areas
    return [baseClasses, 'inset-0 w-full h-full'].join(' ');
  } else {
    const heightClass = drawerOptions.value.full
      ? 'h-[calc(100dvh-(44px+env(safe-area-inset-top)))]'
      : 'max-h-[calc(100dvh-(44px+env(safe-area-inset-top)))]';
    return [
      baseClasses,
      'bottom-0 left-0 w-full rounded-t-[20px] shadow-[0_-5px_20px_rgba(0,0,0,0.1)]',
      heightClass
    ].join(' ');
  }
});

const transitionName = computed(() => isDesktop.value ? 'slide-left' : 'slide-up');

// ============ SECONDARY DRAWER ============

const secondaryDrawerStyle = computed(() => {
  // Drag transform (mobile only)
  if (secondaryIsDragging.value && !isDesktop.value) {
    return {
      transform: `translateY(${Math.max(0, secondaryCurrentY.value)}px)`,
      transition: 'none'
    };
  }
  return {};
});

const secondaryPanelClasses = computed(() => {
  const baseClasses = 'fixed bg-white z-[1001] flex flex-col will-change-transform';

  if (isDesktop.value) {
    return [
      baseClasses,
      'left-4 top-1/2 -translate-y-1/2 w-[460px] max-w-[90vw] h-[90vh] rounded-2xl shadow-2xl overflow-hidden',
    ].join(' ');
  } else {
    const heightClass = secondaryOptions.value.full
      ? 'h-[calc(100dvh-(44px+env(safe-area-inset-top)))]'
      : 'max-h-[calc(100dvh-(44px+env(safe-area-inset-top)))]';
    return [
      baseClasses,
      'bottom-0 left-0 w-full rounded-t-[20px] shadow-[0_-5px_20px_rgba(0,0,0,0.1)]',
      heightClass
    ].join(' ');
  }
});

const secondaryTransitionName = computed(() => isDesktop.value ? 'slide-left-secondary' : 'slide-up');

// ============ PRIMARY TOUCH HANDLERS ============

const handleTouchStart = (e: TouchEvent) => {
  if (isDesktop.value) return;
  if (!drawerOptions.value.closable) return;

  isDragging.value = true;
  startY.value = e.touches[0].clientY;
  currentY.value = 0;
};

const handleTouchMove = (e: TouchEvent) => {
  if (isDesktop.value) return;
  if (!drawerOptions.value.closable) return;
  if (!isDragging.value) return;

  const touchY = e.touches[0].clientY;
  const delta = touchY - startY.value;

  if (delta > 0) {
    if (e.cancelable) e.preventDefault();
    currentY.value = delta;
  }
};

const handleTouchEnd = () => {
  if (isDesktop.value) return;
  if (!drawerOptions.value.closable) return;
  if (!isDragging.value) return;
  isDragging.value = false;
  if (currentY.value > 120) closeDrawer();
  currentY.value = 0;
};

// ============ SECONDARY TOUCH HANDLERS ============

const handleSecondaryTouchStart = (e: TouchEvent) => {
  if (isDesktop.value) return;
  if (!secondaryOptions.value.closable) return;

  secondaryIsDragging.value = true;
  secondaryStartY.value = e.touches[0].clientY;
  secondaryCurrentY.value = 0;
};

const handleSecondaryTouchMove = (e: TouchEvent) => {
  if (isDesktop.value) return;
  if (!secondaryOptions.value.closable) return;
  if (!secondaryIsDragging.value) return;

  const touchY = e.touches[0].clientY;
  const delta = touchY - secondaryStartY.value;

  if (delta > 0) {
    if (e.cancelable) e.preventDefault();
    secondaryCurrentY.value = delta;
  }
};

const handleSecondaryTouchEnd = () => {
  if (isDesktop.value) return;
  if (!secondaryOptions.value.closable) return;
  if (!secondaryIsDragging.value) return;
  secondaryIsDragging.value = false;
  if (secondaryCurrentY.value > 120) closeDrawer();
  secondaryCurrentY.value = 0;
};

// ============ OVERLAY HANDLERS ============

const handleOverlayClick = () => {
  // If secondary is open, this overlay is behind it and won't receive clicks.
  // If only primary is open, close it.
  if (drawerOptions.value.closable && isInteractive.value) {
    closeDrawer();
  }
};

const handleSecondaryOverlayClick = () => {
  if (secondaryOptions.value.closable && secondaryIsInteractive.value) {
    closeDrawer();
  }
};

// ============ INTERACTIVITY GUARDS ============

watch(isOpen, (newVal) => {
  if (newVal) {
    isInteractive.value = false;
    setTimeout(() => {
      isInteractive.value = true;
    }, 100);
  }
});

watch(secondaryOpen, (newVal) => {
  if (newVal) {
    secondaryIsInteractive.value = false;
    // Long enough to swallow the second click of a double-click on the row that
    // opened this drawer — otherwise that click lands on the fresh backdrop and
    // closes the drawer the user just opened.
    setTimeout(() => {
      secondaryIsInteractive.value = true;
    }, 400);
  }
});

</script>

<style scoped lang="scss">
@use "../assets/shared" as *;

// Smooth transition for primary minimize/un-minimize effect
.primary-minimize-transition {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1),
              opacity 0.35s ease;
}

// Mobile: slide up from bottom
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.4s cubic-bezier(0.32, 0.72, 0, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

// Desktop: slide in from left (primary)
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.35s ease;
}

.slide-left-enter-from,
.slide-left-leave-to {
  transform: translate(-100%, -50%);
  opacity: 0;
}

.slide-left-enter-to,
.slide-left-leave-from {
  transform: translate(0, -50%);
  opacity: 1;
}

// Desktop: slide in from left (secondary — same animation, separate name to avoid conflicts)
.slide-left-secondary-enter-active,
.slide-left-secondary-leave-active {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.35s ease;
}

.slide-left-secondary-enter-from,
.slide-left-secondary-leave-to {
  transform: translate(-100%, -50%);
  opacity: 0;
}

.slide-left-secondary-enter-to,
.slide-left-secondary-leave-from {
  transform: translate(0, -50%);
  opacity: 1;
}
</style>
